import { useRef, useState } from "react";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import styled from "styled-components";
import {
  Box,
  ContentWrapper,
  FileInputContainer,
  FileNameDisplay,
  FileUploadIconStyled,
  HiddenInput,
  LabelText,
  UploadLabel,
} from "../../../../styles/FileUpload";
import { FlexContainer } from "../../../../styles/PersonalInfoStep";
import { alertMessage } from "../../../../services/alertService";
import { ALERT_TITLES } from "../../../../constants";

// Styled components for MUI icons
const StyledDeleteIcon = styled(DeleteOutlinedIcon)({
  fontSize: "24px",
  color: "#E39435",
  cursor: "pointer",
  transition: "color 0.2s ease-in-out",
  "&:hover": {
    color: "#c47a1e",
  },
  "&:focus-visible": {
    outline: "2px solid #E39435",
    borderRadius: "4px",
  },
});

// Error message component
const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 0.75rem;
  margin-top: 4px;
  min-height: 20px;
`;

export interface FileType {
  ImageName?: string;
  name?: string;
  type?: string;
  size?: number;
  lastModified?: number;
  lastModifiedDate?: Date;
  webkitRelativePath?: string;
  [key: string]: any;
}

interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

interface FileUploadProps {
  label: string;
  files: FileType[] | null | undefined;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDelete: () => void;
  inputId: string;
  multiple?: boolean;
  required?: boolean;
  accept?: string;
  maxSizeMB?: number;
}

interface FileUploadSectionProps {
  user: {
    sfaLicense: FileType[];
  };
  handleChangeSFALicense: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const FileUploadSignup: React.FC<FileUploadProps> = ({
  label,
  files,
  onChange,
  onDelete,
  inputId,
  multiple = false,
  required = false,
  accept = "*/*",
  maxSizeMB = 5,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  // File validation helper
  const validateFile = (file: File): FileValidationResult => {
    const maxSize = maxSizeMB * 1024 * 1024; // Convert MB to bytes

    if (file.size > maxSize) {
      // return {
      //   isValid: false,
      //   error: `File size should not exceed ${maxSizeMB}MB`
      // };
      alertMessage(
        ALERT_TITLES.ERROR,
        `File size should not exceed ${maxSizeMB}MB`
      );
      return { isValid: false };
    } else {
      // Check file type if accept is specified
      if (accept !== "*/*") {
        const acceptedTypes = accept
          .split(",")
          .map((type) => type.trim().toLowerCase());
        const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
        const fileType = file.type.toLowerCase();

        const isTypeValid = acceptedTypes.some(
          (type) =>
            type === `.${fileExtension}` ||
            type === fileType ||
            (type.startsWith(".") &&
              fileExtension &&
              type.endsWith(fileExtension)) ||
            (type.endsWith("/*") && fileType.startsWith(type.split("/*")[0]))
        );

        if (!isTypeValid) {
          return {
            isValid: false,
            error: `Invalid file type. Accepted types: ${accept}`,
          };
        }
      }

      return { isValid: true };
    }
  };

  // Handle file change with validation
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null); // Reset previous errors

    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    const fileList = Array.from(e.target.files);
    const validationResults = fileList.map(validateFile);
    const invalidFiles = validationResults.filter((result) => !result.isValid);

    if (invalidFiles.length > 0) {
      // setError(invalidFiles[0].error || "Invalid file");
      // alertMessage(ALERT_TITLES.ERROR, invalidFiles[0].error || "Invalid file");
      return;
    }

    const validFiles = fileList.filter(
      (_, index) => validationResults[index].isValid
    );

    if (validFiles.length > 0) {
      const newEvent = {
        ...e,
        target: {
          ...e.target,
          files: multiple ? validFiles : validFiles[0],
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      onChange(newEvent);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    setError(null);
    onDelete();
  };

  const hasFiles = files && files.length > 0;
  const fileInputKey = hasFiles
    ? `file-input-${files[0]?.name || "has-files"}`
    : "file-input-empty";

  return (
    <FileInputContainer>
      <ContentWrapper>
        <Box $mb={hasFiles ? "4px" : "0"} $justifyContent="space-between">
          <LabelText>
            {label}
            {required && <span aria-hidden="true">*</span>}
          </LabelText>
        </Box>
        {hasFiles && (
          <FileNameDisplay>
            {files.length > 1 ? (
              <>
                {files.map((file, index) => (
                  <span key={index}>
                    {file.ImageName || file.name}
                    {index < files.length - 1 && ", "}
                  </span>
                ))}
              </>
            ) : (
              <span> {files[0].ImageName || files[0].name}</span>
            )}
          </FileNameDisplay>
        )}
        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
      </ContentWrapper>

      <FlexContainer className="right">
        <UploadLabel htmlFor={inputId} aria-label={`Upload ${label}`}>
          <FileUploadIconStyled />
        </UploadLabel>
        {hasFiles && (
          <StyledDeleteIcon
            onClick={handleDelete}
            aria-label={`Remove ${files[0].name || "selected file"}`}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleDelete(e as any);
              }
            }}
          />
        )}
      </FlexContainer>

      <HiddenInput
        key={fileInputKey}
        ref={fileInputRef}
        multiple={multiple}
        type="file"
        id={inputId}
        aria-label={label}
        accept={accept}
        onChange={handleFileChange}
        aria-invalid={!!error}
        aria-describedby={error ? `${inputId}-error` : undefined}
      />
    </FileInputContainer>
  );
};

// FileUploadSection component using the new FileUpload component
export const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  user,
  handleChangeSFALicense,
}) => {
  const handleDelete = () => {
    handleChangeSFALicense({
      target: { name: "sfaLicense", value: null },
    } as any);
  };

  return (
    <FileUploadSignup
      label="SFA License"
      files={user?.sfaLicense || []}
      onChange={handleChangeSFALicense}
      onDelete={handleDelete}
      inputId="sfa-license-upload"
      required={true}
      accept=".pdf,.jpg,.jpeg,.png"
      maxSizeMB={5}
    />
  );
};

export default FileUploadSignup;
